import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Crown, 
  Zap, 
  ShoppingBag, 
  ArrowRight, 
  CheckCircle,
  AlertTriangle,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrialConversionModal = ({ isOpen, onClose, trialUsage = {} }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const scenes = trialUsage?.scenes_generated || 0;
  const proStudio = trialUsage?.pro_scene_studio_uses || 0;
  const coverage = trialUsage?.coverage_reports_generated || 0;

  const handleSubscribe = async (planId) => {
    setLoading(planId);
    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Payment error:', error);
    } finally {
      setLoading(null);
    }
  };

  const handleOneTimePurchase = async (planId) => {
    setLoading(planId);
    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/payments/create-onetime-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Payment error:', error);
    } finally {
      setLoading(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative max-w-2xl w-full bg-gradient-to-b from-background to-secondary/30 border border-white/10 p-8 overflow-y-auto max-h-[90vh]"
        >
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            
            <h2 className="text-2xl font-black text-white mb-2">Your Pro Trial Has Ended</h2>
            <p className="text-muted-foreground">
              Choose how to continue your screenwriting journey
            </p>
          </div>

          {/* Usage Stats */}
          {(scenes > 0 || proStudio > 0 || coverage > 0) && (
            <div className="bg-secondary/50 border border-white/10 p-4 mb-6">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                During Your Trial, You:
              </h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-black text-primary">{scenes}</div>
                  <div className="text-xs text-muted-foreground">Scenes Generated</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-purple-400">{proStudio}</div>
                  <div className="text-xs text-muted-foreground">Pro Studio Uses</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-emerald-400">{coverage}</div>
                  <div className="text-xs text-muted-foreground">Coverage Reports</div>
                </div>
              </div>
            </div>
          )}

          {/* Plan Options */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {/* Basic */}
            <div className="bg-primary/10 border border-primary p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-white">Basic</h4>
              </div>
              <div className="text-2xl font-black text-white mb-2">$3.99<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
              <ul className="space-y-1 mb-4">
                <li className="flex items-center gap-1 text-xs text-muted-foreground">
                  <CheckCircle className="w-3 h-3 text-green-400" />
                  5 scenes/day
                </li>
                <li className="flex items-center gap-1 text-xs text-muted-foreground">
                  <CheckCircle className="w-3 h-3 text-green-400" />
                  Full 3-Act structure
                </li>
              </ul>
              <Button 
                onClick={() => handleSubscribe('basic_monthly')}
                disabled={loading === 'basic_monthly'}
                className="w-full bg-primary text-black hover:bg-primary/90 text-xs py-2"
              >
                Subscribe
              </Button>
            </div>

            {/* Pro - Highlighted */}
            <div className="bg-yellow-500/10 border-2 border-yellow-500 p-4 relative">
              <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-0.5">KEEP PRO</span>
              </div>
              <div className="flex items-center gap-2 mb-2 pt-2">
                <Crown className="w-4 h-4 text-yellow-500" />
                <h4 className="font-bold text-white">Pro</h4>
              </div>
              <div className="text-2xl font-black text-white mb-2">$9.99<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
              <ul className="space-y-1 mb-4">
                <li className="flex items-center gap-1 text-xs text-yellow-100">
                  <CheckCircle className="w-3 h-3 text-yellow-400" />
                  Unlimited scenes
                </li>
                <li className="flex items-center gap-1 text-xs text-yellow-100">
                  <CheckCircle className="w-3 h-3 text-yellow-400" />
                  Pro Scene Studio
                </li>
                <li className="flex items-center gap-1 text-xs text-yellow-100">
                  <CheckCircle className="w-3 h-3 text-yellow-400" />
                  Coverage Reports
                </li>
              </ul>
              <Button 
                onClick={() => handleSubscribe('pro_monthly')}
                disabled={loading === 'pro_monthly'}
                className="w-full bg-yellow-500 text-black hover:bg-yellow-400 text-xs py-2"
              >
                Keep Pro
              </Button>
            </div>

            {/* Script Pass Pro */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 p-4">
              <div className="flex items-center gap-2 mb-2">
                <ShoppingBag className="w-4 h-4 text-emerald-400" />
                <h4 className="font-bold text-white">Script Pass Pro</h4>
              </div>
              <div className="text-2xl font-black text-white mb-2">$39.99<span className="text-sm font-normal text-muted-foreground"> once</span></div>
              <ul className="space-y-1 mb-4">
                <li className="flex items-center gap-1 text-xs text-muted-foreground">
                  <CheckCircle className="w-3 h-3 text-emerald-400" />
                  Pro for one project
                </li>
                <li className="flex items-center gap-1 text-xs text-muted-foreground">
                  <CheckCircle className="w-3 h-3 text-emerald-400" />
                  No subscription
                </li>
              </ul>
              <Button 
                onClick={() => handleOneTimePurchase('single_script_pro')}
                disabled={loading === 'single_script_pro'}
                className="w-full bg-emerald-500 text-black hover:bg-emerald-400 text-xs py-2"
              >
                Buy Once
              </Button>
            </div>
          </div>

          {/* Continue Free Option */}
          <div className="text-center">
            <button
              onClick={onClose}
              className="text-sm text-muted-foreground hover:text-white transition-colors"
            >
              Continue with free tier (limited features)
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TrialConversionModal;
